import React, { useState, useEffect } from 'react';
import { Video, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import axios from '../../utils/axiosConfig';

const FeaturedVideoManager = () => {
  const [videoData, setVideoData] = useState({
    video_url: '',
    title: 'Featured Product Video',
    description: '',
    is_active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchVideo();
  }, []);

  const fetchVideo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/featured-video`);
      setVideoData(response.data);
    } catch (error) {
      console.error('Error fetching video:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractVideoId = (url) => {
    // Extract YouTube video ID from various URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleSave = async () => {
    if (!videoData.video_url) {
      toast({
        title: "Error",
        description: "Please enter a video URL",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/admin/featured-video`, videoData);
      toast({
        title: "Success",
        description: "Featured video updated successfully"
      });
    } catch (error) {
      console.error('Error saving video:', error);
      toast({
        title: "Error",
        description: "Failed to update video",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getEmbedUrl = () => {
    const videoId = extractVideoId(videoData.video_url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Video className="w-8 h-8 text-red-600" />
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Featured Video Management</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Manage the featured supplement product video shown on the blog homepage</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Video Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Video URL *</label>
                <Input
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={videoData.video_url}
                  onChange={(e) => setVideoData({ ...videoData, video_url: e.target.value })}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Paste YouTube video URL</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Title</label>
                <Input
                  type="text"
                  placeholder="Featured Product Video"
                  value={videoData.title}
                  onChange={(e) => setVideoData({ ...videoData, title: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Description</label>
                <textarea
                  placeholder="Brief description about your product..."
                  value={videoData.description}
                  onChange={(e) => setVideoData({ ...videoData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg min-h-[100px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                <h3 className="font-bold text-sm mb-3 text-gray-700 dark:text-gray-300">Button Links</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                      "Explore More" Button URL
                    </label>
                    <Input
                      type="text"
                      placeholder="https://example.com/products"
                      value={videoData.explore_more_url || ''}
                      onChange={(e) => setVideoData({ ...videoData, explore_more_url: e.target.value })}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Leave empty to disable the button
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">
                      "Learn More" Button URL
                    </label>
                    <Input
                      type="text"
                      placeholder="https://example.com/about"
                      value={videoData.learn_more_url || ''}
                      onChange={(e) => setVideoData({ ...videoData, learn_more_url: e.target.value })}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Leave empty to disable the button
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <button
                  onClick={() => setVideoData({ ...videoData, is_active: !videoData.is_active })}
                  className={`p-2 rounded-lg transition-colors ${
                    videoData.is_active 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300' 
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {videoData.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{videoData.is_active ? 'Active' : 'Inactive'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {videoData.is_active ? 'Video is visible on blog page' : 'Video is hidden from blog page'}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Preview</h2>
            
            {getEmbedUrl() ? (
              <div className="space-y-4">
                <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src={getEmbedUrl()}
                    title="Video preview"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-red-50 to-gray-50 dark:from-red-900/20 dark:to-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">{videoData.title}</h3>
                  {videoData.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{videoData.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Video className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">Enter a valid YouTube URL to see preview</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="p-6">
          <h3 className="font-bold mb-2 text-gray-900 dark:text-white">📌 Instructions:</h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <li>Paste your YouTube video URL (e.g., https://www.youtube.com/watch?v=wV8BeJpgkwM)</li>
            <li>The video will appear on the blog homepage in a prominent featured section</li>
            <li>Toggle "Active" to show/hide the video from the blog page</li>
            <li>Changes are reflected immediately after saving</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FeaturedVideoManager;
